// src/pages/FAQPage.jsx
import React, { useState } from 'react';
import {
  Container,
  Row,
  Col,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionBody,
  Breadcrumb,
  BreadcrumbItem
} from 'reactstrap';
import { Link } from 'react-router-dom';
import './FAQPage.css';
import PublicHeader from '../../Layout/PublicHeader';
import Footer from '../../Layout/VerticalLayout/Footer';
import Mining from './Gifs/Mining.gif';
import Wild from './Gifs/Wild.gif';
import Catch from './Gifs/Catch.gif';
import { FaGem, FaFistRaised, FaCheckCircle, FaInfoCircle, FaStar, FaFileAlt } from 'react-icons/fa';

const FAQPage = () => {
  const [openId, setOpenId] = useState('0');
  const toggle = (id) => setOpenId(openId === id ? null : id);
  const asset = (path) => `${process.env.PUBLIC_URL}${path}`;

  return (
    <>
      <PublicHeader />
      <Container className="faq-page py-5">
        <Row>
          <Col>
            <Breadcrumb>
              <BreadcrumbItem><Link to="/">Home</Link></BreadcrumbItem>
              <BreadcrumbItem active>FAQ</BreadcrumbItem>
            </Breadcrumb>
            <h2 className="faq-title mb-4">Frequently Asked Questions</h2>
          </Col>
        </Row>
        <Row>
          <Col md={{ size: 8, offset: 2 }}>
            {/* General Section */}
            <h4 className="faq-category"><FaInfoCircle className="category-icon" />General</h4>
            <Accordion open={openId} toggle={toggle} flush>
              <AccordionItem>
                <AccordionHeader targetId="1">
                  What is Pokémon Radiance?
                </AccordionHeader>
                <AccordionBody accordionId="1">
                  Pokémon Radiance is a player-driven, economy-focused Pokémon world launching on September 6, 2025. We introduce unique variant Pokémon—like Ruby, Emerald, Sapphire, Golden, Shadow, and Diamond—each with their own style and rarity:
                  <div className="type-examples">
                    {[
                      { label: 'Ruby', img: '/sprites/RubyMewtwo.png' },
                      { label: 'Emerald', img: '/sprites/EmeraldYveltal.png' },
                      { label: 'Sapphire', img: '/sprites/SapphireLugia.png' },
                      { label: 'Golden', img: '/sprites/GoldenGiratina.png' },
                      { label: 'Shadow', img: '/sprites/ShadowKyurem.png' },
                      { label: 'Diamond', img: '/sprites/DiamondRayquaza.png' }
                    ].map((type) => (
                      <div key={type.label} className="type-card">
                        <img src={asset(type.img)} alt={`${type.label} variant`} className="type-img" />
                        <span>{type.label}</span>
                      </div>
                    ))}
                  </div>
                  Each variant is strictly capped—if only 8 players claim a Golden Munchlax, only 8 appear in collectors’ inventories. After the promotion ends, we add 20% of that total into our <strong>Economy account</strong> (so 8 claimed → 2 more minted). Color chests draw exclusively from this Economy account, keeping supply dynamic. While individual copies can vanish when accounts go inactive, this reserve ensures every variant release maintains a stable presence—no total wipe-out (special event–only releases remain separate).
                </AccordionBody>
              </AccordionItem>
              <AccordionItem>
                <AccordionHeader targetId="3">
                  Where can I report bugs?
                </AccordionHeader>
                <AccordionBody accordionId="3">
                  You can report bugs on our Discord server under the #bug-reports channel.
                </AccordionBody>
              </AccordionItem>
              <AccordionItem>
                <AccordionHeader targetId="4">
                  How do events work?
                </AccordionHeader>
                <AccordionBody accordionId="4">
                  Events are scheduled challenges where players collaborate or compete in real-time. Details and schedule are posted on our events calendar and Discord announcements.
                </AccordionBody>
              </AccordionItem>
              <AccordionItem>
                <AccordionHeader targetId="5">
                  Can I play on mobile devices?
                </AccordionHeader>
                <AccordionBody accordionId="5">
                  Yes! Pokémon Radiance features a responsive design optimized for desktop and mobile browsers. Simply open the game URL in your mobile browser.
                </AccordionBody>
              </AccordionItem>
              <AccordionItem>
                <AccordionHeader targetId="10">
                  When do updates occur and what is decided?
                </AccordionHeader>
                <AccordionBody accordionId="10">
                  Updates occur periodically and depend on the needs of the players and our roadmap. We try to roll out an update once a month to keep the game fresh and address community feedback.
                </AccordionBody>
              </AccordionItem>
            </Accordion>

            {/* Variants Section */}
            <h4 className="faq-category"><FaStar className="category-icon" />Variants</h4>
            <Accordion open={openId} toggle={toggle} flush>
              <AccordionItem>
                <AccordionHeader targetId="8">
                  Do variant Pokémon give any benefits?
                </AccordionHeader>
                <AccordionBody accordionId="8">
                  Yes! All variant Pokémon have an inherent 5% chance to drop minerals matching their variant type. For example, a Ruby Mewtwo can occasionally drop rubies when battling wild Pokémon.
                </AccordionBody>
              </AccordionItem>
              <AccordionItem>
                <AccordionHeader targetId="11">
                  What are the rules on promo claiming?
                </AccordionHeader>
                <AccordionBody accordionId="11">
                  You are allowed to have an alt account to claim an additional Promo and Map promos, but claiming a Mining Promo with a backup account can result in a ban.
                </AccordionBody>
              </AccordionItem>
              <AccordionItem>
                <AccordionHeader targetId="12">
                  How long do Map promos last?
                </AccordionHeader>
                <AccordionBody accordionId="12">
                  Map promos last until the end of the month.
                </AccordionBody>
              </AccordionItem>
              <AccordionItem>
                <AccordionHeader targetId="13">
                  What happens when the rarity limit reaches for Mining Promos?
                </AccordionHeader>
                <AccordionBody accordionId="13">
                  When the rarity limit is reached for Mining Promos, 20% will get added to Economy and then a new mining promo will be placed.
                </AccordionBody>
              </AccordionItem>
            </Accordion>

            {/* Mining Section */}
            <h4 className="faq-category"><FaGem className="category-icon" />Mining</h4>
            <Accordion open={openId} toggle={toggle} flush>
              <AccordionItem>
                <AccordionHeader targetId="6">
                  How do I acquire gems?
                </AccordionHeader>
                <AccordionBody accordionId="6">
                  Head to the Department Store and buy a pickaxe, then go to Manage Items and equip it. Next, visit any map with rocks to start mining.
                  <div className="faq-gif-container">
                    <img src={Mining} alt="Mining rocks" className="faq-gif" />
                  </div>
                </AccordionBody>
              </AccordionItem>
              <AccordionItem>
                <AccordionHeader targetId="14">
                  How often do Gem Rocks spawn?
                </AccordionHeader>
                <AccordionBody accordionId="14">
                  Gem rocks have a 5% spawn rate, but wearing something like a Ruby Helmet increases the odds of spawning to 15%.
                </AccordionBody>
              </AccordionItem>
            </Accordion>
            
            {/* Battling Section */}
            <h4 className="faq-category"><FaFistRaised className="category-icon" />Battling</h4>
            <Accordion open={openId} toggle={toggle} flush>
              <AccordionItem>
                <AccordionHeader targetId="7">
                  How do I engage in a wild Pokémon battle?
                </AccordionHeader>
                <AccordionBody accordionId="7">
                  To initiate a wild Pokémon battle, open your pouch and select a rock to throw at the Pokémon. There is a 30% chance that the rock will miss the Pokémon. 
                  <div className="faq-gif-container">
                    <img src={Wild} alt="Wild battle" className="faq-gif" />
                  </div>
                </AccordionBody>
              </AccordionItem>
              <AccordionItem>
                <AccordionHeader targetId="15">
                  What is your battle mechanics like?
                </AccordionHeader>
                <AccordionBody accordionId="15">
                  We mimicked our battle system to be close to Pokémon but it's not exact. We are still updating and figuring out the proper way for battles. The game won't fully mimic Pokémon.
                </AccordionBody>
              </AccordionItem>
              <AccordionItem>
                <AccordionHeader targetId="16">
                  What are the drop percentages for Pokémon?
                </AccordionHeader>
                <AccordionBody accordionId="16">
                  Pokémon have a 20% chance to drop shards and other items which are needed to craft TMs which are on the 3rd floor of the Department Store.
                </AccordionBody>
              </AccordionItem>
              <AccordionItem>
                <AccordionHeader targetId="17">
                  How do I heal my Pokémon?
                </AccordionHeader>
                <AccordionBody accordionId="17">
                  You can heal your Pokémon in the /Roster which is under the Your Pokémon Section.
                </AccordionBody>
              </AccordionItem>
              <AccordionItem>
                <AccordionHeader targetId="18">
                  How do I apply my EV candies?
                </AccordionHeader>
                <AccordionBody accordionId="18">
                  Go to the Roster section, select the Pokémon you want to apply the EV candies to, and then click on the EV Training.
                </AccordionBody>
              </AccordionItem>
              <AccordionItem>
                <AccordionHeader targetId="23">
                  How do moves work?
                </AccordionHeader>
                <AccordionBody accordionId="23">
                  By default any Pokémon can learn any move associated with their type. If there are moves that a certain Pokémon can learn outside their type and you want it added to the Pokémon, please notify staff. Also, moves are still a work in progress as we develop the battle animations.
                </AccordionBody>
              </AccordionItem>
            </Accordion>
            
            {/* Catching Section */}
            <h4 className="faq-category"><FaCheckCircle className="category-icon" />Catching</h4>
            <Accordion open={openId} toggle={toggle} flush>
              <AccordionItem>
                <AccordionHeader targetId="9">
                  How do I catch wild Pokémon?
                </AccordionHeader>
                <AccordionBody accordionId="9">
                  To catch a wild Pokémon, open your pouch and select a Pokéball to throw at the Pokémon.
                  <div className="faq-gif-container">
                    <img src={Catch} alt="Catching Pokémon" className="faq-gif" />
                  </div>
                </AccordionBody>
              </AccordionItem>
              <AccordionItem>
                <AccordionHeader targetId="19">
                  What are the shiny odds?
                </AccordionHeader>
                <AccordionBody accordionId="19">
                  The current shiny odds are 1/4096, but a shiny charm can cut the odds in half and be equipped up to 10 times, causing odds to be as high as 25%.
                </AccordionBody>
              </AccordionItem>
            </Accordion>
            
            {/* Rate List Section */}
            <h4 className="faq-category"><FaFileAlt className="category-icon" />Rate List</h4>
            <Accordion open={openId} toggle={toggle} flush>
              <AccordionItem>
                <AccordionHeader targetId="20">
                  What is the Rate List?
                </AccordionHeader>
                <AccordionBody accordionId="20">
                  The Rate List is a document which goes over the prices for colored Pokémon variants in the game economy.
                </AccordionBody>
              </AccordionItem>
              <AccordionItem>
                <AccordionHeader targetId="21">
                  How are prices determined for Pokémon?
                </AccordionHeader>
                <AccordionBody accordionId="21">
                  Typically promos go for 2x the price of what it is going for in the promo center, but ultimately the prices are determined by the community and what the Pokémon trades for.
                </AccordionBody>
              </AccordionItem>
              <AccordionItem>
                <AccordionHeader targetId="22">
                  How often does the Rate List get updated?
                </AccordionHeader>
                <AccordionBody accordionId="22">
                  Depends on activity of the Pokémon but generally once a month.
                </AccordionBody>
              </AccordionItem>
            </Accordion>
          </Col>
        </Row>
      </Container>
      <Footer />
    </>
  );
};

export default FAQPage;
